import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthUser, WorkspaceMembership } from '@app/shared';
import { api } from './api';

/**
 * Auth/session context (§14 auth). Holds the authenticated user + their workspaces,
 * and the currently-selected workspace. Server state (the `me` payload) is fetched
 * with TanStack Query keyed on the token; local UI state (selected workspace) is
 * `useState`. Sign out clears the token and the query cache.
 *
 * The token itself lives in the ApiClient's localStorage store (lib/api.ts); this
 * context tracks whether we HAVE a token so the router can guard routes.
 */

interface AuthContextValue {
  user: AuthUser | null;
  workspaces: WorkspaceMembership[];
  workspaceId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setWorkspaceId: (id: string) => void;
  /** Call after register/login to refresh the session and pick a workspace. */
  onAuthenticated: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState<boolean>(() => api.token != null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);

  const meQuery = useQuery({
    queryKey: ['me', hasToken],
    queryFn: () => api.me(),
    enabled: hasToken,
    retry: false,
  });

  const workspaces = meQuery.data?.workspaces ?? [];
  const workspaceId = selectedWorkspaceId ?? workspaces[0]?.id ?? null;

  const onAuthenticated = useCallback(async () => {
    setHasToken(true);
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    const me = await queryClient.fetchQuery({ queryKey: ['me', true], queryFn: () => api.me() });
    setSelectedWorkspaceId(me.workspaces[0]?.id ?? null);
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await api.logout();
    setHasToken(false);
    setSelectedWorkspaceId(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data?.user ?? null,
      workspaces,
      workspaceId,
      isAuthenticated: hasToken && !meQuery.isError,
      isLoading: hasToken && meQuery.isLoading,
      setWorkspaceId: setSelectedWorkspaceId,
      onAuthenticated,
      signOut,
    }),
    [meQuery.data, meQuery.isError, meQuery.isLoading, workspaces, workspaceId, hasToken, onAuthenticated, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/** The current workspace id, throwing if none — for authed screens that require one. */
export function useWorkspaceId(): string {
  const { workspaceId } = useAuth();
  if (!workspaceId) throw new Error('No workspace selected');
  return workspaceId;
}
